import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../lib/supabaseClient';
import { 
  BarChart2, 
  Filter, 
  Calendar,
  ChevronLeft,
  ChevronRight,
  Search
} from 'lucide-react';

const ResponsesPage = () => {
  const { formId } = useParams();
  const { user } = useAuth();
  const { addToast } = useToast();
  
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(formId || '');
  const [responses, setResponses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({
    totalResponses: 0,
    averageRating: 0,
    responseRate: 0,
  });

  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    if (user) {
      fetchForms();
    }
  }, [user]);

  useEffect(() => {
    if (selectedForm) {
      fetchResponses();
      fetchStats();
      markAllResponsesAsRead();
    }
  }, [selectedForm, currentPage, dateFilter]);

  const markAllResponsesAsRead = async () => {
    if (!selectedForm) return;
    
    try {
      // Get all unread response IDs for this form
      const { data: unreadResponses } = await supabase
        .from('responses')
        .select('id')
        .eq('form_id', selectedForm)
        .is('viewed_at', null);
      
      if (unreadResponses && unreadResponses.length > 0) {
        const responseIds = unreadResponses.map(r => r.id);
        
        const { error } = await supabase.rpc('mark_responses_as_read', {
          response_ids: responseIds
        });
        
        if (error) {
          console.error('Error marking responses as read:', error);
        }
      }
    } catch (error) {
      console.error('Error marking responses as read:', error);
    }
  };

  const fetchForms = async () => {
    try {
      const { data, error } = await supabase
        .from('forms')
        .select('id, name')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setForms(data || []);
      if (!selectedForm && data && data.length > 0) {
        setSelectedForm(data[0].id);
      }
    } catch (error) {addToast('Failed to fetch forms', 'error');
    }
  };

  const fetchResponses = async () => {
    try {
      let query = supabase
        .from('responses')
        .select('*')
        .eq('form_id', selectedForm)
        .order('created_at', { ascending: false });

      if (dateFilter === 'today') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        query = query.gte('created_at', today.toISOString());
      } else if (dateFilter === 'week') {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        query = query.gte('created_at', weekAgo.toISOString());
      } else if (dateFilter === 'month') {
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        query = query.gte('created_at', monthAgo.toISOString());
      }

      const start = (currentPage - 1) * ITEMS_PER_PAGE;
      const end = start + ITEMS_PER_PAGE - 1;
      query = query.range(start, end);

      const { data, error, count } = await query;

      if (error) throw error;
      
      setResponses(data || []);
      setTotalPages(Math.ceil((count || 0) / ITEMS_PER_PAGE));
    } catch (error) {addToast('Failed to fetch responses', 'error');
    }
  };

  const fetchStats = async () => {
    try {
      const { data, error } = await supabase
        .from('responses')
        .select('*')
        .eq('form_id', selectedForm);

      if (error) throw error;

      const total = data.length;
      let ratingSum = 0;
      let ratingCount = 0;

      data.forEach(response => {
        response.responses.forEach(r => {
          if (r.question_type === 'rating') {
            ratingSum += parseInt(r.response);
            ratingCount++;
          }
        });
      });

      setStats({
        totalResponses: total,
        averageRating: ratingCount > 0 ? (ratingSum / ratingCount).toFixed(1) : 0,
        responseRate: '87%',
      });
    } catch (error) {}
  };

  const downloadResponses = () => {
    try {
      const csvData = responses.map(response => {
        const formattedResponse = {
          'Submission Date': new Date(response.created_at).toLocaleString(),
        };
        
        response.responses.forEach(r => {
          formattedResponse[r.question_text] = r.response;
        });
        
        return formattedResponse;
      });

      const headers = Object.keys(csvData[0]);
      const csv = [
        headers.join(','),
        ...csvData.map(row => headers.map(header => JSON.stringify(row[header])).join(','))
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('hidden', '');
      a.setAttribute('href', url);
      a.setAttribute('download', 'responses.csv');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {addToast('Failed to download responses', 'error');
    }
  };

  const getSelectedFormName = () => {
    const form = forms.find(f => f.id === selectedForm);
    return form ? form.name : '';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Responses</h1>
          <p className="text-sm text-gray-500">
            View and analyze feedback from your customers
          </p>
        </div>

        <div className="mt-4 md:mt-0">
          <button
            onClick={downloadResponses}
            disabled={responses.length === 0}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <BarChart2 size={16} className="mr-2" />
            Download CSV
          </button>
        </div>
      </div>

      {/* Form selector and filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="p-4 sm:p-6 grid grid-cols-1 gap-4 md:grid-cols-4">
          <div>
            <label htmlFor="form-select" className="block text-sm font-medium text-gray-700">
              Select Form
            </label>
            <select
              id="form-select"
              value={selectedForm}
              onChange={(e) => setSelectedForm(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              {forms.map((form) => (
                <option key={form.id} value={form.id}>
                  {form.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="date-filter" className="block text-sm font-medium text-gray-700">
              Date Range
            </label>
            <select
              id="date-filter"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="all">All time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 days</option>
              <option value="month">Last 30 days</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700">
              Search Responses
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                placeholder="Search responses..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BarChart2 className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Responses
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {stats.totalResponses}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BarChart2 className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Average Rating
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {stats.averageRating}/5
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Filter className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Response Rate
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {stats.responseRate}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Responses list */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Responses for {getSelectedFormName()}
          </h3>
        </div>
        
        {responses.length > 0 ? (
          <>
            <ul className="divide-y divide-gray-200">
              {responses.map((response) => (
                <li key={response.id} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <p className="ml-2 text-sm text-gray-500">
                        {formatDate(response.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2">
                    {response.responses.map((r, index) => (
                      <div key={index} className="mb-2">
                        <p className="text-sm font-medium text-gray-600">
                          {r.question_text}
                        </p>
                        <p className="mt-1 text-sm text-gray-900">
                          {r.response || 'No response'}
                        </p>
                      </div>
                    ))}
                  </div>
                </li>
              ))}
            </ul>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <BarChart2 size={48} className="mx-auto text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No responses yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Responses will appear here once customers submit feedback.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResponsesPage;